import React, { useState } from 'react';
import validator from 'validator';
import {
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AlertPOP from '../../../routes/AlertPOP';
import Loader from '../../../routes/Loader';
import { useNavigate } from 'react-router-dom';
import { Postrequest } from '../../../context/AxiosRequest';

// Styles object
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    p: 2,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
    p: 5,
    maxWidth: '450px',
    width: '100%',
  },
  header: {
    textAlign: 'center',
    mb: 4,
  },
  logo: {
    width: '120px',
    mb: 2,
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#1a1a2e',
    mb: 1,
  },
  subtitle: {
    fontSize: '0.95rem',
    color: '#666',
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  inputField: {
    mb: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  },
  button: {
    mt: 2,
    py: 1.5,
    borderRadius: '10px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1rem',
  },
  stepIndicator: {
    display: 'flex',
    justifyContent: 'center',
    gap: 1,
    mb: 3,
  },
  stepDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#e0e0e0',
    transition: 'all 0.3s ease',
  },
  stepDotActive: {
    backgroundColor: '#667eea',
    transform: 'scale(1.2)',
  },
  stepDotCompleted: {
    backgroundColor: '#4caf50',
  },
  backLink: {
    textAlign: 'center',
    mt: 3,
    color: '#667eea',
    cursor: 'pointer',
    fontSize: '0.9rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  successIcon: {
    fontSize: '4rem',
    color: '#4caf50',
    textAlign: 'center',
    mb: 2,
  },
};

function ForgettenPassword() {
  const navigate = useNavigate();

  // Step management: 1 = email, 2 = verification code, 3 = new password
  const [currentStep, setCurrentStep] = useState(1);

  // Form data
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Password visibility toggles
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Alert and loader states
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [transition, setTransition] = useState(undefined);
  const [openLoader, setOpenLoader] = useState(false);

  // Success state
  const [resetSuccess, setResetSuccess] = useState(false);

  // Handle alert
  const handleClick = () => {
    setTransition(() => 'TransitionLeft');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Step 1: Request verification code
  const handleRequestCode = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      handleClick();
      setMessage('Please enter your email address.');
      return;
    }

    if (!validator.isEmail(email)) {
      handleClick();
      setMessage('Please enter a valid email address.');
      return;
    }

    setOpenLoader(true);

    try {
      const response = await Postrequest('/auth/request_reset_code/', { email }, '');

      if (response.status) {
        handleClick();
        setMessage('Verification code sent to your email!');
        setCurrentStep(2);
      } else {
        handleClick();
        setMessage(response.textStatus || 'Email not found. Please check and try again.');
      }
    } catch (error) {
      handleClick();
      setMessage('An error occurred. Please try again.');
    }

    setOpenLoader(false);
  };

  // Step 2: Verify the code
  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      handleClick();
      setMessage('Please enter the verification code.');
      return;
    }

    if (verificationCode.length < 4) {
      handleClick();
      setMessage('Please enter a valid verification code.');
      return;
    }

    setOpenLoader(true);

    try {
      const response = await Postrequest('/auth/verify_reset_code/', { email, code: verificationCode }, '');

      if (response.status) {
        handleClick();
        setMessage('Code verified successfully!');
        setCurrentStep(3);
      } else {
        handleClick();
        setMessage(response.textStatus || 'Invalid verification code. Please try again.');
      }
    } catch (error) {
      handleClick();
      setMessage('An error occurred. Please try again.');
    }

    setOpenLoader(false);
  };

  // Step 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!newPassword.trim() || !confirmPassword.trim()) {
      handleClick();
      setMessage('Please fill in all password fields.');
      return;
    }

    if (newPassword.length < 6) {
      handleClick();
      setMessage('Password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      handleClick();
      setMessage('Passwords do not match. Please try again.');
      return;
    }

    setOpenLoader(true);

    try {
      const response = await Postrequest('/auth/reset_password/', {
        email,
        code: verificationCode,
        newPassword,
      }, '');

      if (response.status) {
        handleClick();
        setMessage('Password reset successfully!');
        setResetSuccess(true);
      } else {
        handleClick();
        setMessage(response.textStatus || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      handleClick();
      setMessage('An error occurred. Please try again.');
    }

    setOpenLoader(false);
  };

  // Navigate back to login
  const goToLogin = () => {
    navigate('/');
  };

  // Resend verification code
  const handleResendCode = async () => {
    setOpenLoader(true);

    try {
      const response = await Postrequest('/pub/request_reset_code/', { email }, '');

      if (response.status) {
        handleClick();
        setMessage('New verification code sent to your email!');
      } else {
        handleClick();
        setMessage(response.textStatus || 'Failed to resend code. Please try again.');
      }
    } catch (error) {
      handleClick();
      setMessage('An error occurred. Please try again.');
    }

    setOpenLoader(false);
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <Box sx={styles.stepIndicator}>
      {[1, 2, 3].map((step) => (
        <Box
          key={step}
          sx={{
            ...styles.stepDot,
            ...(currentStep === step ? styles.stepDotActive : {}),
            ...(currentStep > step ? styles.stepDotCompleted : {}),
          }}
        />
      ))}
    </Box>
  );

  // Get step title and subtitle
  const getStepContent = () => {
    if (resetSuccess) {
      return {
        title: 'Password Reset!',
        subtitle: 'Your password has been successfully reset. You can now login with your new password.',
      };
    }

    switch (currentStep) {
      case 1:
        return {
          title: 'Forgot Password?',
          subtitle: 'Enter your email address and we will send you a verification code to reset your password.',
        };
      case 2:
        return {
          title: 'Enter Verification Code',
          subtitle: `We sent a verification code to ${email}. Please enter the code below.`,
        };
      case 3:
        return {
          title: 'Create New Password',
          subtitle: 'Please enter your new password. Make sure it is strong and secure.',
        };
      default:
        return { title: '', subtitle: '' };
    }
  };

  const { title, subtitle } = getStepContent();

  return (
    <>
      <AlertPOP Open={open} handleClose={handleClose} transition={transition} message={message} />
      <Loader OpenLoader={openLoader} />

      <Box sx={styles.container}>
        <Paper sx={styles.card} elevation={0}>
        <Box sx={styles.header}>
          <Box
            component="img"
            src="/assets/newksslb.jpg"
            alt="Logo"
            sx={styles.logo}
          />
          {!resetSuccess && renderStepIndicator()}
          <Typography sx={styles.title}>{title}</Typography>
          <Typography sx={styles.subtitle}>{subtitle}</Typography>
        </Box>

        {/* Success State */}
        {resetSuccess && (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={styles.successIcon}>✓</Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={styles.button}
              onClick={goToLogin}
            >
              Go to Login
            </Button>
          </Box>
        )}

        {/* Step 1: Email Input */}
        {!resetSuccess && currentStep === 1 && (
          <Box component="form" sx={styles.form} onSubmit={handleRequestCode}>
            <TextField
              label="Email Address"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={styles.inputField}
              type="email"
              placeholder="Enter your registered email"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={styles.button}
            >
              Send Verification Code
            </Button>
            <Typography sx={styles.backLink} onClick={goToLogin}>
              ← Back to Login
            </Typography>
          </Box>
        )}

        {/* Step 2: Verification Code Input */}
        {!resetSuccess && currentStep === 2 && (
          <Box component="form" sx={styles.form} onSubmit={handleVerifyCode}>
            <TextField
              label="Verification Code"
              variant="outlined"
              fullWidth
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              sx={styles.inputField}
              placeholder="Enter the code sent to your email"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={styles.button}
            >
              Verify Code
            </Button>
            <Typography
              sx={{ ...styles.backLink, mt: 1 }}
              onClick={handleResendCode}
            >
              Didn't receive the code? Resend
            </Typography>
            <Typography sx={styles.backLink} onClick={() => setCurrentStep(1)}>
              ← Change Email
            </Typography>
          </Box>
        )}

        {/* Step 3: New Password Input */}
        {!resetSuccess && currentStep === 3 && (
          <Box component="form" sx={styles.form} onSubmit={handleResetPassword}>
            <TextField
              label="New Password"
              variant="outlined"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={styles.inputField}
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Enter new password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              variant="outlined"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={styles.inputField}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={styles.button}
            >
              Reset Password
            </Button>
            <Typography sx={styles.backLink} onClick={() => setCurrentStep(2)}>
              ← Back to Verification
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
    </>
  );
}

export default ForgettenPassword;